import React, { FC } from 'react'
import { useFormik } from 'formik'
import { Box, FormHelperText, Grid, TextField, Typography } from '@material-ui/core'
import ButtonWithPreloader from 'UI/Button/ButtonWithPreloader'
import RequiredOption from 'UI/Select/RequiredOption'
import { IExternalDeal } from 'typescript/interfaces/deals'
import * as yup from 'yup'
import { INVALID_PRICE_FORMAT, NUMBERS_ONLY, REQUIRED_FIELD } from 'constants/errors'
import { priceReg } from 'constants/regExp'
import FileDragNDrop from 'components/FileDragNDrop'
import { useMutation } from 'react-query'
import { getDownloadUrl, uploadToS3 } from 'services/storage'
import { useSnackbar } from 'notistack'
import DecimalTextField from 'UI/TextField/DecimalTextField'

interface IExternalDealsFormProps {
  initialValues?: IExternalDeal
  onSubmit: (values: IExternalDeal) => void
  onCancel: () => void
}

const validationSchema = yup.object().shape({
  funder: yup.string().required(REQUIRED_FIELD),
  payoffBalance: yup
    .string()
    .required(REQUIRED_FIELD)
    .typeError(NUMBERS_ONLY)
    .matches(priceReg, INVALID_PRICE_FORMAT),
  wireInstructionsUrl: yup.string().test(
    'wire-instructions-required',
    REQUIRED_FIELD,
    function (value) {
      return Boolean(value || this.parent.wireInstructionsFile)
    },
  ),
  payoffLetterUrl: yup.string().test(
    'payoff-letter-required',
    REQUIRED_FIELD,
    function (value) {
      return Boolean(value || this.parent.payoffLetterFile)
    },
  ),
})

const defaultInitialValues: IExternalDeal = {
  id: 0,
  funder: undefined,
  payoffBalance: undefined,
  wireInstructionsUrl: undefined,
  payoffLetterUrl: undefined,
}

export const getExternalDealDocName = (url: string): string | undefined => {
  const match = decodeURIComponent(url).match(/([^/]+?)(?:_[A-Za-z0-9+/=]+)?(\.[^./]+)$/)

  return match ? match[1] + (match[2] || '') : undefined
}

const ExternalDealsForm: FC<IExternalDealsFormProps> = ({ onSubmit, onCancel, initialValues = defaultInitialValues }) => {
  const snack = useSnackbar()

  const { values, errors, touched, dirty, handleChange, handleSubmit, setFieldValue } = useFormik<
    IExternalDeal & { wireInstructionsFile?: File; payoffLetterFile?: File }
  >({
    initialValues,
    validationSchema,
    enableReinitialize: false,
    onSubmit: async ({ wireInstructionsFile, payoffLetterFile, ...res }) => {
      if (!dirty) {
        onCancel()
        return
      }

      try {
        if (wireInstructionsFile) {
          res.wireInstructionsUrl = await uploadDocument(wireInstructionsFile)
        }
        if (payoffLetterFile) {
          res.payoffLetterUrl = await uploadDocument(payoffLetterFile)
        }
      } catch {
        snack.enqueueSnackbar(<Typography>Failed to upload document</Typography>, { variant: 'error' })
        return
      }

      onSubmit({
        ...res,
        payoffBalance: Number(res.payoffBalance),
      })
    },
  })

  const [uploadDocument, { isLoading }] = useMutation(
    async (file: File) => {
      const uploadUrl = await getDownloadUrl({ DocumentName: file.name, DocumentDestination: 'ExternalDeal', DestinationObjectId: 0 })
      await uploadToS3(file, uploadUrl.data)

      return uploadUrl.data.split('?')[0]
    },
    {
      onError: () => {
        snack.enqueueSnackbar(<Typography>Something went wrong</Typography>)
      },
    },
  )

  return (
    <form onSubmit={handleSubmit}>
      <Box mb={6}>
        <Typography variant="body1">External Deal</Typography>
      </Box>
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <TextField
            variant="outlined"
            fullWidth
            name="funder"
            onChange={handleChange}
            value={values.funder ?? ''}
            label={<RequiredOption label="Funder" />}
            InputLabelProps={{ shrink: true }}
            error={Boolean(touched.funder && errors.funder)}
            helperText={touched.funder && errors.funder}
          />
        </Grid>
        <Grid item xs={12}>
          <DecimalTextField
            variant="outlined"
            fullWidth
            name="payoffBalance"
            onChange={handleChange}
            value={values.payoffBalance ?? ''}
            error={Boolean(touched.payoffBalance && errors.payoffBalance)}
            helperText={touched.payoffBalance && errors.payoffBalance}
            label={<RequiredOption label="Payoff Balance" />}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12}>
          <FileDragNDrop
            title={<>Wire Instructions <Box component="span" color="error.main">*</Box></>}
            name="WireInstructions"
            maxFiles={1}
            error={Boolean(touched.wireInstructionsUrl && errors.wireInstructionsUrl)}
            onDrop={(files) => setFieldValue('wireInstructionsFile', files[0])}
            files={values.wireInstructionsFile ? [values.wireInstructionsFile] : []}
            uploadedFiles={
              values.wireInstructionsUrl ? [{ id: 0, description: getExternalDealDocName(values.wireInstructionsUrl) }] : []
            }
            onRemove={() => {
              setFieldValue('wireInstructionsFile', undefined)
              setFieldValue('wireInstructionsUrl', undefined)
            }}
            onRemoveUploaded={() => {
              setFieldValue('wireInstructionsFile', undefined)
              setFieldValue('wireInstructionsUrl', undefined)
            }}
          />
          {touched.wireInstructionsUrl && errors.wireInstructionsUrl && (
            <FormHelperText error>{errors.wireInstructionsUrl}</FormHelperText>
          )}
        </Grid>
        <Grid item xs={12}>
          <FileDragNDrop
            title={<>Payoff Letter <Box component="span" color="error.main">*</Box></>}
            name="PayoffLetter"
            maxFiles={1}
            error={Boolean(touched.payoffLetterUrl && errors.payoffLetterUrl)}
            onDrop={(files) => setFieldValue('payoffLetterFile', files[0])}
            files={values.payoffLetterFile ? [values.payoffLetterFile] : []}
            uploadedFiles={
              values.payoffLetterUrl ? [{ id: 0, description: getExternalDealDocName(values.payoffLetterUrl) }] : []
            }
            onRemove={() => {
              setFieldValue('payoffLetterFile', undefined)
              setFieldValue('payoffLetterUrl', undefined)
            }}
            onRemoveUploaded={() => {
              setFieldValue('payoffLetterFile', undefined)
              setFieldValue('payoffLetterUrl', undefined)
            }}
          />
          {touched.payoffLetterUrl && errors.payoffLetterUrl && (
            <FormHelperText error>{errors.payoffLetterUrl}</FormHelperText>
          )}
        </Grid>
      </Grid>
      <Box mt={6}>
        <ButtonWithPreloader loading={isLoading} type="submit" color="primary" variant="contained">
          Save
        </ButtonWithPreloader>
      </Box>
    </form>
  )
}

export default ExternalDealsForm
